import React, { useState, useEffect, useRef } from "react"

import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"

import { pauseAllVideos } from "../../../../../utils/general"

import PlayIcon from "../../../../../assets/icons/PlayIcon.svg"
import PauseIcon from "../../../../../assets/icons/PauseIcon.svg"
import SoundIcon from "../../../../../assets/icons/nova/sound-min.svg"
import MuteIcon from "../../../../../assets/icons/nova/sound-mute.svg"

import { VideoContainer, ButtonWrapper } from "./story.styled"

const StoryCardVideo = ({
  video,
  videoPoster,
  name,
  reverse,
  isSingleStory,
}) => {
  const videoRef = useRef(null)

  const [videoDetails, setVideoDetails] = useState({
    progress: 0,
    muted: false,
    isVideoPlaying: false,
    videoStartTime: null,
  })

  const handleMuteIconClick = () => {
    setVideoDetails((prevState) => ({
      ...prevState,
      muted: !videoDetails.muted,
    }))
  }

  const handleProgress = (e) => {
    const currentTime = e.target.currentTime
    const duration = e.target.duration

    if (!isNaN(duration) && duration !== 0) {
      const progress = (currentTime / duration) * 100
      setVideoDetails((prevState) => ({
        ...prevState,
        progress: progress,
      }))
    } else {
      setVideoDetails((prevState) => ({
        ...prevState,
        progress: 0,
      }))
    }
  }

  useEffect(() => {
    // If the video is playing, set the currentTime to the saved start time
    if (videoDetails.isVideoPlaying && videoRef.current) {
      videoRef.current.currentTime = videoDetails.videoStartTime || 0
    }
  }, [videoDetails.videoStartTime, videoDetails.isVideoPlaying])

  const togglePlayPause = () => {
    segmentEvent("Video Played", {
      module_state: videoDetails.isVideoPlaying ? "Paused" : "Play",
      type: "button",
      video_name: name + " Results Video",
    })

    if (videoRef.current) {
      if (videoRef.current.paused) {
        pauseAllVideos()
        // Set the currentTime to the previously saved time (if any)
        if (videoDetails.videoStartTime !== null) {
          videoRef.current.currentTime = videoDetails.videoStartTime
        }

        videoRef.current.play()
        setVideoDetails((prevState) => ({
          ...prevState,
          isVideoPlaying: true,
        }))
      } else {
        // Save the current video time and pause the video
        setVideoDetails((prevState) => ({
          ...prevState,
          isVideoPlaying: false,
          videoStartTime: videoRef.current.currentTime, // Save the paused time
        }))

        videoRef.current.pause()
      }
      videoRef.current.focus()
    }
  }

  const handleVideoEnded = () => {
    videoRef.current.pause()
    videoRef.current.currentTime = 0
    videoRef.current.load()
    setVideoDetails((prevState) => ({
      ...prevState,
      isVideoPlaying: false,
      videoStartTime: null,
    }))
  }

  return (
    <VideoContainer
      order={[1, 1, 1, 1, reverse ? 2 : 1]}
      width={isSingleStory ? [1, null, null, 0.5] : 1}
    >
      <video
        key={video}
        ref={videoRef}
        width="100%"
        height="auto"
        poster={videoPoster}
        autoPlay={videoDetails.isVideoPlaying}
        loop={false}
        playsInline={true}
        onTimeUpdate={handleProgress}
        muted={videoDetails.muted}
        onClick={togglePlayPause}
        onKeyDown={(e) => {
          if (e.keyCode === 32 || e.keyCode === 13) {
            e.preventDefault()
            togglePlayPause()
          }
        }}
        onEnded={handleVideoEnded}
        className={`scroll-margin-style ${
          isSingleStory ? "single-story-video" : ""
        }`}
        preload="none"
        tabIndex="0"
      >
        <source src={video} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <ButtonWrapper>
        {videoDetails.isVideoPlaying ? (
          <button
            className="sound-mute-icon-button"
            type="submit"
            onClick={handleMuteIconClick}
            tabIndex="-1"
          >
            {videoDetails.muted ? (
              <span className="mute-icon-container">
                <MuteIcon />
              </span>
            ) : (
              <span className="sound-icon-container">
                <SoundIcon />
              </span>
            )}
          </button>
        ) : null}

        <button
          className="play-pause-icon-button"
          type="submit"
          onClick={togglePlayPause}
          tabIndex="-1"
        >
          {!videoDetails.isVideoPlaying ? (
            <span className="play-icon-container">
              <PlayIcon />
            </span>
          ) : (
            <span className="pause-icon-container">
              <PauseIcon />
            </span>
          )}
        </button>
      </ButtonWrapper>
      {videoDetails.isVideoPlaying ? (
        <div className="progressbar-wrapper">
          <div className="duration-wrapper">
            <div className="duration-bar">
              <div
                className="duration-bar-progress"
                style={{ width: `${videoDetails.progress}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </VideoContainer>
  )
}

export default StoryCardVideo
